import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Search, Send, Paperclip, X, Loader2, MessageSquare,
  FileText, Image as ImageIcon, Download, Plus, ChevronLeft,
} from 'lucide-react';

const ROLE_LABELS = {
  admin: 'Admin',
  subcontractor: 'Sous-traitant',
  commercial: 'Commercial',
  client: 'Client',
};

const ROLE_COLORS = {
  admin: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  subcontractor: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  commercial: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  client: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function AttachmentChip({ att, onRemove }) {
  const isImg = (att.content_type || '').startsWith('image/');
  const I = isImg ? ImageIcon : FileText;
  return (
    <div className="flex items-center gap-2 bg-zinc-800/80 border border-zinc-700 rounded-sm px-2 py-1 text-xs">
      <I className="w-3 h-3 text-blue-400 shrink-0" />
      <span className="truncate max-w-[160px] text-zinc-200">{att.filename}</span>
      {onRemove && (
        <button onClick={onRemove} className="text-zinc-500 hover:text-rose-400" data-testid={`remove-attachment-${att.file_id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function AttachmentLink({ att }) {
  const isImg = (att.content_type || '').startsWith('image/');
  const token = localStorage.getItem('pp_access_token') || '';
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/download/${att.file_id}?auth=${encodeURIComponent(token)}`;
  if (isImg) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-2" data-testid={`attachment-img-${att.file_id}`}>
        <img src={url} alt={att.filename} className="max-h-48 rounded-sm border border-zinc-700 hover:opacity-90 transition-opacity" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className="mt-2 inline-flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-700 transition-colors max-w-full"
       data-testid={`attachment-file-${att.file_id}`}>
      <FileText className="w-4 h-4 text-blue-400 shrink-0" />
      <span className="truncate flex-1">{att.filename}</span>
      <Download className="w-3 h-3 text-zinc-500 shrink-0" />
    </a>
  );
}

function ConversationList({ threads, contacts, activeId, onSelect, onStartNew, search, setSearch, mode }) {
  const [showContacts, setShowContacts] = useState(false);
  const filteredThreads = threads.filter(t => {
    if (!search) return true;
    const other = t.other || {};
    return (other.name || '').toLowerCase().includes(search.toLowerCase())
      || (other.company || '').toLowerCase().includes(search.toLowerCase());
  });
  const filteredContacts = contacts.filter(c => {
    if (!search) return true;
    return (c.name || '').toLowerCase().includes(search.toLowerCase())
      || (c.company || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className={`flex flex-col bg-zinc-950 ${mode === 'page' ? 'border border-zinc-800 rounded-sm' : ''}`}>
      <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
        <Search className="w-4 h-4 text-zinc-500 shrink-0" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="bg-transparent border-0 h-7 focus-visible:ring-0 px-0 text-sm placeholder:text-zinc-600"
          data-testid="chat-search-input"
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowContacts(s => !s)}
          className="h-7 px-2 text-zinc-400 hover:text-zinc-100 shrink-0"
          data-testid="toggle-contacts-btn"
          title={showContacts ? "Retour aux conversations" : "Nouvelle conversation"}
        >
          {showContacts ? <ChevronLeft className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="chat-list">
        {showContacts ? (
          <>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-900">
              Démarrer une conversation
            </div>
            {filteredContacts.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-500">Aucun contact disponible</div>
            ) : filteredContacts.map(c => (
              <button
                key={c.user_id}
                onClick={() => { onStartNew(c); setShowContacts(false); }}
                className="w-full px-3 py-2.5 border-b border-zinc-900 flex items-center gap-2.5 hover:bg-zinc-900/70 transition-colors text-left"
                data-testid={`contact-${c.user_id}`}
              >
                <Avatar className="w-9 h-9 shrink-0">
                  <AvatarFallback className={`text-xs font-bold ${ROLE_COLORS[c.role] || ''}`}>
                    {(c.name || '?').slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate font-medium">{c.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    <span className={`inline-block px-1.5 py-0 rounded-sm text-[9px] uppercase tracking-wider mr-1 ${ROLE_COLORS[c.role]}`}>{ROLE_LABELS[c.role]}</span>
                    {c.company || c.email}
                  </div>
                </div>
              </button>
            ))}
          </>
        ) : filteredThreads.length === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Aucune conversation.</p>
            <p className="mt-1">Cliquez sur <strong>+</strong> pour démarrer.</p>
          </div>
        ) : filteredThreads.map(t => (
          <button
            key={t.id}
            onClick={() => onSelect(t)}
            className={`w-full px-3 py-2.5 border-b border-zinc-900 flex items-center gap-2.5 hover:bg-zinc-900/70 transition-colors text-left ${activeId === t.id ? 'bg-zinc-900/80' : ''}`}
            data-testid={`thread-item-${t.id}`}
          >
            <Avatar className="w-9 h-9 shrink-0">
              <AvatarFallback className={`text-xs font-bold ${ROLE_COLORS[t.other?.role] || ''}`}>
                {(t.other?.name || '?').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-zinc-200 truncate font-medium">{t.other?.name || '—'}</div>
                <span className="text-[10px] text-zinc-500 shrink-0">{formatTime(t.last_message_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] text-zinc-500 truncate">{t.last_message_preview || 'Nouvelle conversation'}</div>
                {t.unread_count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-amber-500 text-zinc-950 text-[10px] font-bold rounded-full" data-testid={`thread-unread-${t.id}`}>
                    {t.unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ThreadView({ thread, onBack, mode, onMessageSent }) {
  const { user: currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastFetchRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!thread?.id) return;
    try {
      const { data } = await api.get(`/messages/threads/${thread.id}/messages`, { params: { limit: 200 } });
      setMessages(data);
      lastFetchRef.current = data.length > 0 ? data[data.length - 1].created_at : null;
      try { await api.post(`/messages/threads/${thread.id}/read`); } catch { /* ignore */ }
    } catch (e) {
      toast.error('Erreur de chargement des messages');
    }
  }, [thread?.id]);

  // Initial load + polling every 4s
  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 4000);
    return () => clearInterval(id);
  }, [fetchAll]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newAtts = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append('file', f);
        const { data } = await api.post('/messages/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newAtts.push({
          file_id: data.id,
          filename: data.original_filename,
          content_type: data.content_type,
          size: data.size,
        });
      } catch (e) {
        toast.error(`Erreur upload ${f.name}: ${e?.response?.data?.detail || ''}`);
      }
    }
    setAttachments(prev => [...prev, ...newAtts]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const send = async () => {
    if (sending) return;
    const text = content.trim();
    if (!text && attachments.length === 0) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${thread.id}/messages`, {
        content: text,
        attachments,
      });
      setContent('');
      setAttachments([]);
      await fetchAll();
      onMessageSent && onMessageSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!thread) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm bg-zinc-950">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Sélectionne une conversation
        </div>
      </div>
    );
  }

  const meId = currentUser?._id || currentUser?.id || '';

  return (
    <div className="flex-1 flex flex-col bg-zinc-950 min-h-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-3 shrink-0">
        {mode === 'drawer' && (
          <Button size="sm" variant="ghost" onClick={onBack} className="h-8 w-8 p-0" data-testid="back-to-list-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        <Avatar className="w-9 h-9">
          <AvatarFallback className={`text-xs font-bold ${ROLE_COLORS[thread.other?.role] || ''}`}>
            {(thread.other?.name || '?').slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-100 truncate" data-testid="active-thread-name">{thread.other?.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">
            <span className={`inline-block px-1.5 py-0 rounded-sm text-[9px] uppercase tracking-wider mr-1 ${ROLE_COLORS[thread.other?.role]}`}>{ROLE_LABELS[thread.other?.role]}</span>
            {thread.other?.company || thread.other?.email}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-scroll">
        {messages.length === 0 ? (
          <div className="text-center text-xs text-zinc-500 mt-8">
            Aucun message — démarrez la conversation 👋
          </div>
        ) : messages.map(m => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[78%] ${mine ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                {!mine && (
                  <div className="text-[10px] text-zinc-500 px-1">{m.sender_name}</div>
                )}
                <div className={`rounded-sm px-3 py-2 text-sm ${mine
                  ? 'bg-amber-500/15 border border-amber-500/30 text-amber-50'
                  : 'bg-zinc-900 border border-zinc-800 text-zinc-100'}`}>
                  {m.content && <div className="whitespace-pre-wrap break-words">{m.content}</div>}
                  {(m.attachments || []).map(att => (
                    <AttachmentLink key={att.file_id} att={att} />
                  ))}
                </div>
                <div className="text-[10px] text-zinc-600 px-1">{formatTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="border-t border-zinc-800 p-3 bg-zinc-950 shrink-0">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="attachments-preview">
            {attachments.map(a => (
              <AttachmentChip key={a.file_id} att={a} onRemove={() => setAttachments(prev => prev.filter(x => x.file_id !== a.file_id))} />
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
            data-testid="file-input"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-9 w-9 p-0 text-zinc-400 hover:text-amber-400 shrink-0"
            data-testid="attach-btn"
            title="Joindre des fichiers"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          </Button>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Écris un message... (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
            rows={1}
            className="flex-1 resize-none bg-zinc-900 border-zinc-800 min-h-[36px] max-h-[120px] py-2 text-sm"
            data-testid="message-input"
          />
          <Button
            size="sm"
            onClick={send}
            disabled={sending || (!content.trim() && attachments.length === 0)}
            className="h-9 bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold shrink-0"
            data-testid="send-message-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ChatPanel({ mode = 'drawer', initialThreadId = null, onUnreadChange }) {
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/threads');
      setThreads(data);
      const totalUnread = data.reduce((s, t) => s + (t.unread_count || 0), 0);
      onUnreadChange && onUnreadChange(totalUnread);
      return data;
    } catch {
      return [];
    }
  }, [onUnreadChange]);

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/contacts');
      setContacts(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [data] = await Promise.all([fetchThreads(), fetchContacts()]);
      if (initialThreadId) {
        const t = (data || []).find(x => x.id === initialThreadId);
        if (t) setActiveThread(t);
      }
      setLoading(false);
    })();
  }, [fetchThreads, fetchContacts, initialThreadId]);

  // Poll threads every 6s to refresh previews + unread badge
  useEffect(() => {
    const id = setInterval(fetchThreads, 6000);
    return () => clearInterval(id);
  }, [fetchThreads]);

  const handleStartNew = async (contact) => {
    try {
      const { data } = await api.post('/messages/threads/open', { other_user_id: contact.user_id });
      await fetchThreads();
      setActiveThread({ ...data, other: contact });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversation non autorisée');
    }
  };

  const handleSelect = (t) => setActiveThread(t);

  const handleMessageSent = () => fetchThreads();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500 text-sm">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  if (mode === 'page') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-0 h-[calc(100vh-180px)] min-h-[480px]" data-testid="chat-page">
        <ConversationList
          threads={threads} contacts={contacts}
          activeId={activeThread?.id}
          onSelect={handleSelect}
          onStartNew={handleStartNew}
          search={search} setSearch={setSearch}
          mode="page"
        />
        <div className="border border-zinc-800 border-l-0 md:border-l rounded-sm md:rounded-l-none flex flex-col min-h-0">
          <ThreadView thread={activeThread} mode="page" onMessageSent={handleMessageSent} />
        </div>
      </div>
    );
  }

  // Drawer mode: list OR thread (one at a time on mobile)
  return (
    <div className="flex flex-col h-full" data-testid="chat-drawer-content">
      {!activeThread ? (
        <ConversationList
          threads={threads} contacts={contacts}
          activeId={null}
          onSelect={handleSelect}
          onStartNew={handleStartNew}
          search={search} setSearch={setSearch}
          mode="drawer"
        />
      ) : (
        <ThreadView
          thread={activeThread}
          onBack={() => setActiveThread(null)}
          mode="drawer"
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  );
}
